interface StorePreviewProps {
  name: string;
  logo: string;
  banner: string;
  color: string;
  description: string;
  subdomain: string;
  showProducts: boolean;
  showServices: boolean;
}

/**
 * Aperçu en direct d'un mini-site public du marchand.
 * Réagit instantanément au logo, à la couleur, au nom et au type de compte.
 * (Les couleurs de marque du marchand sont volontairement en style inline,
 * car il s'agit de SON identité, pas du thème de l'application.)
 */
export default function StorePreview({
  name,
  logo,
  banner,
  color,
  description,
  subdomain,
  showProducts,
  showServices,
}: StorePreviewProps) {
  const brand = color || '#155C9C';
  const navItems = [
    { label: 'Accueil', active: true },
    ...(showProducts ? [{ label: 'Nos produits', active: false }] : []),
    ...(showServices ? [{ label: 'Nos services', active: false }] : []),
    { label: 'Contact', active: false },
  ];

  const cards = [
    ...(showProducts
      ? [
          { tag: 'Produit', title: 'Article vedette', price: '199', icon: 'ri-shopping-bag-3-line' },
          { tag: 'Produit', title: 'Nouveauté', price: '349', icon: 'ri-shopping-bag-3-line' },
        ]
      : []),
    ...(showServices
      ? [
          { tag: 'Service', title: 'Prestation premium', price: '250', icon: 'ri-service-line' },
        ]
      : []),
  ].slice(0, 3);

  return (
    <div className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
      {/* Barre de fenêtre / URL */}
      <div className="flex items-center gap-2 px-3 py-2 bg-background-100 border-b border-background-200/70">
        <div className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
        </div>
        <div className="flex-1 flex items-center gap-1.5 px-2 py-1 rounded-md bg-background-50 border border-background-200/70 min-w-0">
          <i className="ri-lock-line text-[10px] text-accent-500 flex-shrink-0"></i>
          <span className="text-[11px] text-foreground-500 truncate">{subdomain || 'maboutique.zifek.fr'}</span>
        </div>
      </div>

      {/* En-tête du site */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-background-50" style={{ borderBottom: `2px solid ${brand}` }}>
        <div className="flex items-center gap-2 min-w-0">
          {logo ? (
            <span className="w-7 h-7 rounded-md overflow-hidden bg-background-100 border border-background-200/60 flex-shrink-0">
              <img src={logo} alt="" className="w-full h-full object-contain p-0.5" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </span>
          ) : (
            <span className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: brand }}>
              {(name || 'M').charAt(0).toUpperCase()}
            </span>
          )}
          <span className="text-sm font-bold truncate" style={{ color: brand }}>{name || 'Ma boutique'}</span>
        </div>
        <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
          {navItems.map((item) => (
            <span
              key={item.label}
              className="text-[11px] whitespace-nowrap"
              style={{ color: item.active ? brand : 'oklch(var(--foreground-600))', fontWeight: item.active ? 600 : 400 }}
            >
              {item.label}
            </span>
          ))}
        </div>
        <div className="sm:hidden w-6 h-6 flex items-center justify-center">
          <i className="ri-menu-line text-foreground-500 text-sm"></i>
        </div>
      </div>

      {/* Bannière */}
      <div className="relative h-28 overflow-hidden">
        {banner ? (
          <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${brand} 0%, ${brand}cc 55%, ${brand}88 100%)` }}></div>
        )}
        <div className="absolute inset-0 bg-black/25"></div>
        <div className="relative h-full flex flex-col justify-center px-4 text-center">
          <h3 className="text-white text-sm sm:text-base font-bold leading-tight drop-shadow">{name || 'Ma boutique'}</h3>
          <p className="text-white/85 text-[10px] mt-1 line-clamp-2 max-w-xs mx-auto">
            {description || 'Bienvenue sur ma boutique en ligne'}
          </p>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-3">
        {cards.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {cards.map((c) => (
              <div key={c.title + c.price} className="border border-background-200/70 rounded-lg overflow-hidden bg-background-50">
                <div className="h-14 bg-background-100 flex items-center justify-center">
                  <i className={`${c.icon} text-xl text-foreground-300`}></i>
                </div>
                <div className="p-2">
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${brand}1a`, color: brand }}>{c.tag}</span>
                  <p className="text-[11px] font-semibold text-foreground-800 mt-1 truncate">{c.title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] font-bold" style={{ color: brand }}>{c.price} DH</span>
                    <button className="text-[9px] px-1.5 py-0.5 rounded-full text-white whitespace-nowrap" style={{ backgroundColor: brand }}>
                      Voir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 text-center">
            <i className="ri-store-2-line text-2xl text-foreground-300"></i>
            <p className="text-[11px] text-foreground-400 mt-1">Aucun contenu à afficher</p>
          </div>
        )}

        {/* Bouton principal */}
        <button className="mt-3 w-full py-2 rounded-md text-white text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: brand }}>
          <i className="ri-shopping-cart-2-line mr-1"></i>
          Commander maintenant
        </button>
      </div>

      {/* Pied de page */}
      <div className="px-4 py-2 text-center" style={{ borderTop: `1px solid ${brand}33` }}>
        <span className="text-[9px] text-foreground-400">© {new Date().getFullYear()} {name || 'Ma boutique'} · Propulsé par Zifek</span>
      </div>
    </div>
  );
}